import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  Grid,
  Divider,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Edit,
  AddPhotoAlternate,
  Delete,
  CloudUpload,
  Add,
  Close,
  LocationOn
} from '@mui/icons-material';
import { InputAdornment } from "@mui/material";
import { styled } from '@mui/system';
import apiEndpoints from '../../apiconfig';

// Styled Components
const GradientCard = styled(Card)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  position: 'relative',
  overflow: 'visible',
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
  },
}));

const ContentImage = styled('img')({
  width: '100%',
  height: '200px',
  borderRadius: "10px",
  marginTop: "8px",
  marginBottom: "8px",
  objectFit: 'cover',
  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
});

const MapPreview = styled('iframe')({
  width: '100%',
  height: '200px',
  border: 'none',
  borderRadius: '8px',
  marginTop: '16px'
});

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "& fieldset": {
      borderColor: "rgba(0, 184, 148, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0, 184, 148, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00b894",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(0, 0, 0, 0.6)",
    "&.Mui-focused": {
      color: "#00b894",
    },
  },
});

const BenefitsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '8px'
});

const OurEventsSlider = () => {
  const [events, setEvents] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [currentBenefit, setCurrentBenefit] = useState('');
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [eventData, setEventData] = useState({
    event_name: '',
    main_card_disc: '',
    page_description: '',
    main_card_image: '',
    event_date: '',
    event_time: '',
    locationUrl: '',
    address: '',
    city: '',
    state: '',
    mobile: '',
    event_benefits: [],
  });

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiEndpoints.events);
      const data = await response.json();
      if (response.ok) {
        setEvents(data); // PHP already returns parsed benefits
      } else {
        throw new Error(data.message || 'Failed to fetch events');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventData(prev => ({ ...prev, [name]: value }));
  };

   const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setEventData(prev => ({ ...prev, main_card_image: '' }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleOpenAddModal = () => {
    setEventData({
      event_name: '',
      main_card_disc: '',
      page_description: '',
      main_card_image: '',
      event_date: '',
      event_time: '',
      locationUrl: '',
      address: '',
      city: '',
      state: '',
      mobile: '',
      event_benefits: [],
    });
    setSelectedFile(null);
    setImagePreview(null);
    setCurrentEvent(null);
    setShowEventModal(true);
  };

  const handleOpenEditModal = (event) => {
    setCurrentEvent(event);
    setEventData({
      event_name: event.event_name,
      main_card_disc: event.main_card_disc,
      page_description: event.page_description,
      main_card_image: event.main_card_image ? 
        `${apiEndpoints.idproof_path}${event.main_card_image}` : '',
      event_date: event.event_date.split('T')[0], // Format date for input
      event_time: event.event_time,
      locationUrl: event.locationUrl,
      address: event.address,
      city: event.city,
      state: event.state,
      mobile: event.mobile,
      event_benefits: event.event_benefits || [],
    });
    setSelectedFile(null);
    setImagePreview(event.main_card_image ? 
      `${apiEndpoints.idproof_path}${event.main_card_image}` : null);
    setShowEventModal(true);
  };

  const handleSubmitEvent = async () => {
    setIsLoading(true);

    try {
      const formData = new FormData();

      // Append all form data
      Object.keys(eventData).forEach(key => {
        if (key === 'event_benefits') {
          formData.append(key, JSON.stringify(eventData[key]));
        } else if (key !== 'main_card_image') {
          formData.append(key, eventData[key]);
        }
      });

      // Handle file upload
      if (selectedFile) {
        formData.append('main_card_image', selectedFile);
      }

      // For PUT/DELETE, add _method parameter
      if (currentEvent) {
        formData.append('_method', 'PUT');
        formData.append('id', currentEvent.id);
      }

      const url = apiEndpoints.events;
      const method = 'POST'; // Always use POST with _method override

      const response = await fetch(url, {
        method,
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: currentEvent ? 'Event updated successfully!' : 'Event added successfully!',
          severity: 'success'
        });
        setShowEventModal(false);
        fetchEvents();
      } else {
        throw new Error(result.message || 'Failed to save event');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('_method', 'DELETE');
      formData.append('id', eventId);

      const response = await fetch(apiEndpoints.events, {
        method: 'POST', // Using POST with _method=DELETE
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Event deleted successfully!',
          severity: 'success'
        });
        fetchEvents();
      } else {
        throw new Error(result.message || 'Failed to delete event');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Benefits handling
  const handleBenefitKeyDown = (e) => {
    if (e.key === 'Enter' && currentBenefit.trim()) {
      e.preventDefault();
      setEventData(prev => ({
        ...prev,
        event_benefits: [...prev.event_benefits, currentBenefit.trim()]
      }));
      setCurrentBenefit('');
    }
  };

  const removeBenefit = (index) => {
    setEventData(prev => ({
      ...prev,
      event_benefits: prev.event_benefits.filter((_, i) => i !== index)
    }));
  };

  // Extract embed URL from Google Maps URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('google.com') || urlObj.hostname.includes('maps.app.goo.gl')) {
        // Handle Google Maps URLs
        if (urlObj.pathname.includes('/maps/embed')) {
          return url; // Already an embed URL
        }

        // Extract place ID or coordinates
        const placeIdMatch = url.match(/place\/([^/]+)/);
        const queryParams = new URLSearchParams(urlObj.search);
        const llParam = queryParams.get('ll');

        if (placeIdMatch) {
          return `https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=place_id:${placeIdMatch[1]}`;
        } else if (llParam) {
          const [lat, lng] = llParam.split(',');
          return `https://www.google.com/maps/embed/v1/view?key=YOUR_API_KEY&center=${lat},${lng}&zoom=15`;
        } else {
          // Fallback to just showing the URL in an iframe (may not work due to X-Frame-Options)
          return url;
        }
      }
      return url;
    } catch {
      return url;
    }
  };

  const extractSrcFromIframe = (iframeString) => {
    if (!iframeString) return '';
    const srcMatch = iframeString.match(/src="([^"]*)"/);
    return srcMatch ? srcMatch[1] : iframeString;
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end',  mb: 5,}}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenAddModal}
          sx={{
            backgroundColor: '#00b894',
            '&:hover': {
              backgroundColor: '#009975'
            }
          }}
        >
          Add Event
        </Button>
      </Box>

      {isLoading && <LinearProgress color="primary" />}

      <Grid container spacing={3}>
        {events.map((event) => (
          <Grid item xs={12} md={6} key={event.id} width={550}>
            <GradientCard>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" fontWeight="bold">
                    {event.event_name}
                  </Typography>
                  <Box>
                    <IconButton onClick={() => handleOpenEditModal(event)} color="primary">
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteEvent(event.id)} color="error">
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>

                {event.main_card_image && (
                  <ContentImage
                    src={`${apiEndpoints.idproof_path}${event.main_card_image}`}
                    alt={event.event_name}
                  />
                )}

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {event.main_card_disc}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date:</strong> {event.event_date}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Time:</strong> {event.event_time}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Address:</strong> {event.address}, {event.city}, {event.state}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Contact:</strong> {event.mobile}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Location:</strong>
                  </Typography>
                  {event.locationUrl && (
                    <>
                      <MapPreview
                        src={getEmbedUrl(extractSrcFromIframe(event.locationUrl))}
                        title="Location Map"
                        allowFullScreen
                      />
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LocationOn />}
                          onClick={() => window.open(extractSrcFromIframe(event.locationUrl), '_blank')}
                        >
                          Open in Maps
                        </Button>
                      </Box>
                    </>
                  )}
                </Grid>

                {event.event_benefits && event.event_benefits.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Benefits:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {event.event_benefits.map((benefit, index) => (
                        <Chip key={index} label={benefit} />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </GradientCard>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Event Modal */}
      <Dialog
        open={showEventModal}
        onClose={() => setShowEventModal(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
            fontWeight: 'bold',
            fontSize: '1.5rem',
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {currentEvent ? 'Edit Event' : 'Create New Event'}
          <IconButton onClick={() => setShowEventModal(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ padding: '24px' }}>
          <Grid container spacing={3} maxHeight={'300px'}>
            {/* Left Column */}
              <StyledTextField
                label="Event Name"
                name="event_name"
                fullWidth
                value={eventData.event_name}
                onChange={handleChange}
                sx={{ mb: 3 }}
              />

              <StyledTextField
                label="Main Card Description"
                name="main_card_disc"
                fullWidth
                multiline
                rows={3}
                value={eventData.main_card_disc}
                onChange={handleChange}
                sx={{ mb: 3 }}
              />

              <StyledTextField
                label="Page Description"
                name="page_description"
                fullWidth
                multiline
                rows={4}
                value={eventData.page_description}
                onChange={handleChange}
                sx={{ mb: 3 }}
              />

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body1" sx={{ mr: 2 }}>
                    Main Card Image:
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={triggerFileInput}
                  >
                    Upload
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Box>

                {/* Show existing image or new preview */}
                {(imagePreview || eventData.main_card_image) && (
                  <Box sx={{ position: 'relative', mt: 1 }}>
                    <ContentImage
                      src={imagePreview || eventData.main_card_image}
                      alt="Event Preview"
                    />
                    <IconButton
                      onClick={() => {
                        if (imagePreview) {
                          setImagePreview(null);
                          setSelectedFile(null);
                        } else {
                          setEventData(prev => ({ ...prev, main_card_image: '' }));
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)'
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                )}
              </Box>

            {/* Right Column */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StyledTextField
                    label="Event Date"
                    name="event_date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={eventData.event_date}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StyledTextField
                    label="Event Time"
                    name="event_time"
                    type="time"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={eventData.event_time}
                    onChange={handleChange}
                  />
                </Grid>

              <StyledTextField
                label="Event Location URL (Place the embed map link)"
                name="locationUrl"
                fullWidth
                value={eventData.locationUrl}
                onChange={handleChange}
                sx={{ mt: 3, mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {eventData.locationUrl && (
                        <IconButton
                          onClick={() => window.open(extractSrcFromIframe(eventData.locationUrl), '_blank')}
                          edge="end"
                          title="Preview Map"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="3" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => window.open('https://maps.google.com', '_blank')}
                        edge="end"
                      >
                        <LocationOn />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <StyledTextField
                label="Address"
                name="address"
                fullWidth
                value={eventData.address}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StyledTextField
                    label="City"
                    name="city"
                    fullWidth
                    value={eventData.city}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StyledTextField
                    label="State"
                    name="state"
                    fullWidth
                    value={eventData.state}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <StyledTextField
                label="Contact Number"
                name="mobile"
                fullWidth
                value={eventData.mobile}
                onChange={handleChange}
                sx={{ mt: 2, mb: 2 }}
              />

              <StyledTextField
                label="Event Benefits (Press Enter to add)"
                value={currentBenefit}
                onChange={(e) => setCurrentBenefit(e.target.value)}
                onKeyDown={handleBenefitKeyDown}
                fullWidth
                sx={{ mb: 1 }}
              />

              <BenefitsContainer>
                {eventData.event_benefits.map((benefit, index) => (
                  <Chip
                    key={index}
                    label={benefit}
                    onDelete={() => removeBenefit(index)}
                    sx={{ mb: 1 }}
                  />
                ))}
              </BenefitsContainer>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setShowEventModal(false)}
            sx={{
              color: '#666',
              fontWeight: 'bold',
              padding: '8px 16px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitEvent}
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: '#00b894',
              fontWeight: 'bold',
              padding: '8px 24px',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#009975',
                boxShadow: 'none'
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            {isLoading ? 'Saving...' : currentEvent ? 'Update Event' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OurEventsSlider;